import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { TemplateService } from '../../services/template.service';
import { buildHeaderFromWebsiteData } from '../../utils/website-preview-header.util';
import {
  convertLinkToPageType as convertLinkToPageTypeFromUtil,
  pageNameToPageType,
} from '../../../shared/utils/preview-navigation.util';
import {
  applyBrandingFromWebsiteJson,
  extractSharedWebsiteJson,
  mapPreviewPagesFromApi,
  mergeWebsiteJsonIntoPage,
} from '../../../shared/utils/preview-json.util';
import { mapContactBusinessHours } from '../../../shared/utils/map-contact-business-hours.util';
import {
  isExternalFooterHref,
  isKnownFooterPageType,
  normalizeFooterPageType,
  resolveFooterQuickLinks,
} from '../../../shared/utils/footer-quick-links.util';

// Shared preview themes from @way/website-themes (same components as the editor canvas)
import { HomePagePreviewComponent } from '@way/website-themes/themes/minimal_website/home-page-preview/home-page-preview.component';
import { ExpressWashPackagesPreviewComponent } from '@way/website-themes/themes/minimal_website/express-wash-packages-preview/express-wash-packages-preview.component';
import { ExpressAutoDetailsPreviewComponent } from '@way/website-themes/themes/minimal_website/express-auto-details-preview/express-auto-details-preview.component';
import { FleetManagementPreviewComponent } from '@way/website-themes/themes/minimal_website/fleet-management-preview/fleet-management-preview.component';
import { CouponsPreviewComponent } from '@way/website-themes/themes/minimal_website/coupons-preview/coupons-preview.component';
import { ModernHomePagePreviewComponent } from '@way/website-themes/themes/modern_theme/home-page-preview/home-page-preview.component';
import { ModernExpressWashPackagesPreviewComponent } from '@way/website-themes/themes/modern_theme/express-wash-packages-preview/express-wash-packages-preview.component';
import { ModernExpressAutoDetailsPreviewComponent } from '@way/website-themes/themes/modern_theme/express-auto-details-preview/express-auto-details-preview.component';
import { ModernFleetManagementPreviewComponent } from '@way/website-themes/themes/modern_theme/fleet-management-preview/fleet-management-preview.component';
import { ModernCouponsPreviewComponent } from '@way/website-themes/themes/modern_theme/coupons-preview/coupons-preview.component';
import { DarkHomePagePreviewComponent } from '@way/website-themes/themes/dark_theme/home-page-preview/home-page-preview.component';
import { DarkExpressWashPackagesPreviewComponent } from '@way/website-themes/themes/dark_theme/express-wash-packages-preview/express-wash-packages-preview.component';
import { DarkExpressAutoDetailsPreviewComponent } from '@way/website-themes/themes/dark_theme/express-auto-details-preview/express-auto-details-preview.component';
import { DarkFleetManagementPreviewComponent } from '@way/website-themes/themes/dark_theme/fleet-management-preview/fleet-management-preview.component';
import { DarkCouponsPreviewComponent } from '@way/website-themes/themes/dark_theme/coupons-preview/coupons-preview.component';
import { ChampsHomePagePreviewComponent } from '@way/website-themes/themes/champs_theme/home-page-preview/home-page-preview.component';
import { ChampsExpressWashPackagesPreviewComponent } from '@way/website-themes/themes/champs_theme/express-wash-packages-preview/express-wash-packages-preview.component';
import { ChampsExpressAutoDetailsPreviewComponent } from '@way/website-themes/themes/champs_theme/express-auto-details-preview/express-auto-details-preview.component';
import { ChampsMembershipsPreviewComponent } from '@way/website-themes/themes/champs_theme/memberships-preview/memberships-preview.component';
import { ChampsFleetManagementPreviewComponent } from '@way/website-themes/themes/champs_theme/fleet-management-preview/fleet-management-preview.component';
import { ChampsCouponsPreviewComponent } from '@way/website-themes/themes/champs_theme/coupons-preview/coupons-preview.component';

@Component({
  selector: 'app-website-preview-page',
  standalone: true,
  imports: [
    CommonModule,
    // Minimal Theme
    HomePagePreviewComponent,
    ExpressWashPackagesPreviewComponent,
    ExpressAutoDetailsPreviewComponent,
    FleetManagementPreviewComponent,
    CouponsPreviewComponent,
    // Modern Theme
    ModernHomePagePreviewComponent,
    ModernExpressWashPackagesPreviewComponent,
    ModernExpressAutoDetailsPreviewComponent,
    ModernFleetManagementPreviewComponent,
    ModernCouponsPreviewComponent,
    // Dark Theme
    DarkHomePagePreviewComponent,
    DarkExpressWashPackagesPreviewComponent,
    DarkExpressAutoDetailsPreviewComponent,
    DarkFleetManagementPreviewComponent,
    DarkCouponsPreviewComponent,
    // Champs Theme
    ChampsHomePagePreviewComponent,
    ChampsExpressWashPackagesPreviewComponent,
    ChampsExpressAutoDetailsPreviewComponent,
    ChampsMembershipsPreviewComponent,
    ChampsFleetManagementPreviewComponent,
    ChampsCouponsPreviewComponent,
  ],
  templateUrl: './website-preview-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./website-preview-page.component.scss'],
})
export class WebsitePreviewPageComponent implements OnInit, OnDestroy {
  templateId: string | null = null;
  websiteId: string | null = null;
  templateData: any = null;
  error: string | null = null;

  // Default website elements for preview
  websiteElements: any[] = [];

  // Section visibility map
  sectionVisibility: Record<string, boolean> = {};

  // Pages data
  pages: any[] = [];

  // Cached page data for smooth navigation
  cachedPages = new Map<string, any>();
  cachedElements = new Map<string, any[]>();
  cachedSectionVisibility = new Map<string, any>();

  // Current page being displayed
  currentPageType = 'home';
  currentPageData: any = null;

  // Primary color from theme
  primaryColor: string | null = null;

  // Heading color from branding
  headingColor: string | null = null;

  // Paragraph/description color from branding
  paragraphColor: string | null = null;

  // Heading font from branding
  headingFont: string | null = null;

  // Body font from branding
  bodyFont: string | null = null;

  // Loading state for smooth transitions — start true so preview never paints a blank page
  isLoading = true;

  // Prevent duplicate navigation calls
  private isNavigating = false;

  // Device type for responsive preview
  deviceType: 'desktop' | 'tablet' | 'mobile' = 'desktop';
  private resizeListener?: () => void;
  private sharedWebsiteJson: any = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private templateService: TemplateService
  ) {}

  /**
   * Initialize component: detect device type, attach resize listener, and subscribe to route and query parameters to load website or template data with fallback.
   * @example
   * gOnInit()
   * void
   * @returns {void} Sets up device detection, window resize handling, and route/query param subscriptions; loads website or template data (or a default template) based on parameters.
   **/
  ngOnInit(): void {
    // Detect initial device type
    this.detectDeviceType();

    // Listen for window resize to update device type
    this.resizeListener = () => this.detectDeviceType();
    window.addEventListener('resize', this.resizeListener);

    // Get websiteId or templateId from route params or query params
    this.route.params.subscribe(params => {
      let templateId = params['id'];
      let pageType = params['pageType'];

      // Normalize legacy /website-preview/:id routes to /preview?id=... (single preview entry point)
      if (
        templateId &&
        !this.route.snapshot.queryParams['websiteId'] &&
        this.router.url.includes('/website-preview/')
      ) {
        const queryParams: Record<string, string> = { id: templateId };
        if (pageType) {
          queryParams['pageType'] = pageType;
        }
        this.router.navigate(['/preview'], { queryParams, replaceUrl: true });
        return;
      }

      // Check query params for websiteId or templateId
      const queryParams = this.route.snapshot.queryParams;
      const websiteId = queryParams['websiteId'];

      if (!templateId) {
        templateId = queryParams['id'] || queryParams['templateId'];
        if (!pageType && queryParams['pageType']) {
          pageType = queryParams['pageType'];
        }
      }

      // Priority: websiteId > templateId
      if (websiteId) {
        this.websiteId = websiteId;
        this.loadWebsiteData(pageType);
      } else if (templateId) {
        this.templateId = templateId;
        this.loadTemplateData(pageType);
      } else {
        // Try to load first available template as fallback
        this.tryLoadDefaultTemplate(pageType);
      }
    });

    // Also subscribe to query params in case they change
    this.route.queryParams.subscribe(queryParams => {
      const websiteId = queryParams['websiteId'];
      const templateId = queryParams['id'] || queryParams['templateId'];

      // Priority: websiteId > templateId
      if (websiteId && !this.websiteId) {
        this.websiteId = websiteId;
        const pageType = queryParams['pageType'] || this.route.snapshot.params['pageType'];
        this.loadWebsiteData(pageType);
      } else if (templateId && !this.templateId && !this.websiteId) {
        this.templateId = templateId;
        const pageType = queryParams['pageType'] || this.route.snapshot.params['pageType'];
        this.loadTemplateData(pageType);
      } else if (
        !this.isLoading &&
        this.pages.length > 0 &&
        ((websiteId && websiteId === this.websiteId) ||
          (templateId && templateId === this.templateId && !this.websiteId))
      ) {
        const pageType = queryParams['pageType'] || 'home';
        if (pageType !== this.currentPageType) {
          this.onNavigationClick(pageType);
        }
      }
    });
  }

  /**
   * Load the first available template from the template service and initialize template-related component state; if no templates are available or an error occurs, set an appropriate error message.
   * @example
   * ryLoadDefaultTemplate('home')
   * void
   * @param {{string|undefined}} {{pageType}} - Optional page type used to load page-specific template data.
   * @returns {{void}} Update component state (templateId, templateData, websiteElements, currentPageData, isLoading, error) and does not return a value.
   **/
  tryLoadDefaultTemplate(pageType?: string): void {
    // Try to get the first available template
    this.error = null;
    this.isLoading = true;
    // Clear previous data to prevent flash of old content
    this.websiteElements = [];
    this.templateData = null;
    this.currentPageData = null;

    this.templateService.getTemplates().subscribe({
      next: response => {
        if (
          response.status &&
          response.data &&
          Array.isArray(response.data) &&
          response.data.length > 0
        ) {
          // Use the first template as default
          const firstTemplate = response.data[0];
          this.templateId = firstTemplate.id;
          this.loadTemplateData(pageType);
        } else {
          this.error =
            'No template ID provided and no templates available. Please provide a template ID in the URL (e.g., /preview?id=template-id).';
          this.isLoading = false;
        }
      },
      error: err => {
        console.error('Error loading templates:', err);
        this.error =
          'No template ID provided. Please provide a template ID in the URL (e.g., /preview?id=template-id or /website-preview/template-id).';
        this.isLoading = false;
      },
    });
  }

  /**
   * Loads website data for the component, initializes pages/template, merges branding and processes template JSON.
   * @example
   * oadWebsiteData('home')
   * undefined
   * @param {{string}} {{initialPageType}} - Optional initial page type to load (e.g. 'home', 'express-wash-packages').
   * @returns {{void}} No return value; updates component state and performs asynchronous loading.
   **/
  retryPreviewLoad(): void {
    const pageType = this.currentPageType || this.route.snapshot.queryParams['pageType'];
    if (this.websiteId) {
      this.loadWebsiteData(pageType);
      return;
    }
    if (this.templateId) {
      this.loadTemplateData(pageType);
    }
  }

  loadWebsiteData(initialPageType?: string): void {
    if (!this.websiteId) return;

    this.error = null;
    this.isLoading = true;
    // Clear previous data to prevent flash of old content
    this.websiteElements = [];
    this.templateData = null;
    this.currentPageData = null;

    this.templateService.getWebsiteById(this.websiteId).subscribe({
      next: resp => {
        try {
          const data = resp?.data;
          if (!data || resp?.status === false) {
            this.error =
              resp?.message ||
              'Website not found. Open preview from the website builder after the site has been saved.';
            this.isLoading = false;
            return;
          }

          // Handle different response structures
          const website = data.website || data;
          if (!website?.id && !website?.template_id && !website?.json_data) {
            this.error = 'Website not found on this environment.';
            this.isLoading = false;
            return;
          }

          const websitePages = Array.isArray(data.website_pages)
            ? data.website_pages
            : Array.isArray(data.pages)
              ? data.pages
              : [];

          this.templateId = website.template_id || null;
          this.pages = mapPreviewPagesFromApi(websitePages);
          if (this.pages.length === 0 && websitePages.length > 0) {
            this.pages = mapPreviewPagesFromApi(
              websitePages.map((page: any) => ({
                ...page,
                active: true,
                json_data: {
                  ...page.json_data,
                  page: { ...(page.json_data?.page || {}), active: true },
                },
              }))
            );
          }

          this.sharedWebsiteJson = extractSharedWebsiteJson(this.pages, website);
          this.applySharedBranding(this.sharedWebsiteJson);

          let pageToLoad = this.pages.find((p: any) => p.isHomePage) || this.pages[0];
          if (initialPageType) {
            const foundPage = this.pages.find((p: any) => p.type === initialPageType);
            if (foundPage) {
              pageToLoad = foundPage;
              this.currentPageType = initialPageType;
            }
          } else {
            this.currentPageType = pageToLoad?.type || 'home';
          }

          if (!pageToLoad) {
            this.error = 'This website has no pages to preview.';
            this.isLoading = false;
            return;
          }

          this.templateData = {
            id: pageToLoad.id,
            name: pageToLoad.name,
            json_data: pageToLoad.json_data,
          };
          this.currentPageData = pageToLoad;

          if (this.templateData?.json_data) {
            this.templateData.json_data = mergeWebsiteJsonIntoPage(
              this.templateData.json_data,
              this.sharedWebsiteJson
            );
            this.processTemplateData(this.templateData.json_data);
          } else if (website?.json_data?.website?.branding?.logo?.url) {
            const headerElement = this.websiteElements.find(el => el.id === 'header');
            if (headerElement) {
              headerElement.content = headerElement.content || {};
              headerElement.content.logo = {
                image: website.json_data.website.branding.logo.url,
                alt: website.json_data.website.branding.logo.alt || 'Logo',
                text: 'Logo',
                link: '/',
              };
            }
            this.updateHeaderNavigation();
          } else {
            this.updateHeaderNavigation();
          }

          this.finalizeDataLoad();
        } catch (err) {
          console.error('Error rendering website preview:', err);
          this.error = 'Failed to render website preview. Please try again.';
          this.isLoading = false;
        }
      },
      error: err => {
        console.error('Error loading website:', err);
        const status = err?.status;
        if (status === 404) {
          this.error =
            'Website not found on this local environment. Open Preview from the website builder you are editing — do not reuse a preview URL from dev or prod.';
        } else if (status === 401) {
          this.error = 'Please sign in again, then open Preview from the website builder.';
        } else {
          this.error =
            err?.error?.message ||
            'Failed to load website. Confirm the API stack is running on localhost:3000 and try again.';
        }
        this.isLoading = false;
      },
    });
  }

  /**
   * Loads template data for the website preview, initializes loading state, selects and processes the requested page (by type) or defaults to the first page, and updates component state and navigation.
   * @example
   * oadTemplateData('home')
   * undefined
   * @param {{string}} {{initialPageType}} - Optional initial page type to load (e.g., 'home', 'contact'); if omitted the first available page is used.
   * @returns {{void}} Void; performs asynchronous data loading and updates component properties (no direct return value).
   **/
  loadTemplateData(initialPageType?: string): void {
    if (!this.templateId) return;

    this.error = null;
    this.isLoading = true;
    // Clear previous data to prevent flash of old content
    this.websiteElements = [];
    this.templateData = null;
    this.currentPageData = null;

    this.templateService.getTemplateById(this.templateId).subscribe({
      next: response => {
        console.log('Template data loaded:', response);

        if (response.status && response.data) {
          const pagesData = Array.isArray(response.data) ? response.data : [response.data];

          // Store all pages (same mapping as published website preview)
          this.pages = mapPreviewPagesFromApi(pagesData);
          this.sharedWebsiteJson = extractSharedWebsiteJson(this.pages);
          this.applySharedBranding(this.sharedWebsiteJson);

          // Determine which page to load based on URL parameter or default to first page
          let pageToLoad = pagesData[0];
          if (initialPageType) {
            const foundPage = this.pages.find(p => p.type === initialPageType);
            if (foundPage) {
              pageToLoad = pagesData.find((p: any) => p.id === foundPage.id) || pagesData[0];
              this.currentPageType = initialPageType;
            }
          } else {
            const homePage = this.pages.find((p: any) => p.isHomePage) || this.pages[0];
            this.currentPageType = homePage?.type || 'home';
            if (homePage) {
              pageToLoad = pagesData.find((p: any) => p.id === homePage.id) || pagesData[0];
            }
          }

          // Use the determined page as the main template data
          this.templateData = {
            ...pageToLoad,
            json_data: mergeWebsiteJsonIntoPage(pageToLoad.json_data, this.sharedWebsiteJson),
          };
          this.currentPageData = this.pages.find(p => p.id === pageToLoad.id);

          // Parse JSON data if available
          if (this.templateData?.json_data) {
            this.processTemplateData(this.templateData.json_data);
          } else {
            // If no json_data, still create navigation from pages
            this.updateHeaderNavigation();
          }

          this.finalizeDataLoad();
        } else {
          this.error = response.message || 'Failed to load template';
          this.isLoading = false;
        }
      },
      error: err => {
        console.error('Error loading template:', err);
        this.error = 'Failed to load template. Please try again.';
        this.isLoading = false;
      },
    });
  }

  /**
   * Finalizes navigation after preview state changes: updates header navigation, routes to the appropriate preview URL based on websiteId or templateId, and clears the navigating flag.
   * @example
   * finalizeNavigation('home')
   * void
   * @param {{string}} {{normalizedPageType}} - The normalized page type to navigate to (e.g., 'home', 'contact').
   * @returns {{void}} No value is returned.
   **/
  private finalizeNavigation(normalizedPageType: string): void {
    this.updateHeaderNavigation();
    if (this.websiteId) {
      if (normalizedPageType === 'home') {
        this.router.navigate(['/preview'], {
          queryParams: { websiteId: this.websiteId },
          replaceUrl: true,
        });
      } else {
        this.router.navigate(['/preview'], {
          queryParams: { websiteId: this.websiteId, pageType: normalizedPageType },
          replaceUrl: true,
        });
      }
    } else if (this.templateId) {
      const queryParams: Record<string, string> = { id: this.templateId };
      if (normalizedPageType !== 'home') {
        queryParams['pageType'] = normalizedPageType;
      }
      this.router.navigate(['/preview'], { queryParams, replaceUrl: true });
    }
    setTimeout(() => {
      this.isNavigating = false;
    }, 100);
  }

  /**
   * Update the header element's navigation based on current pages, preserving an existing logo and ensuring a CTA button exists.
   * @example
   * pdateHeaderNavigation()
   * undefined
   * @param {void} none - No parameters.
   * @returns {void} Updates the header content in-place; does not return a value.
   **/
  updateHeaderNavigation(): void {
    // Find header element and update its navigation
    const headerElement = this.websiteElements.find(el => el.id === 'header');
    if (headerElement && this.pages && this.pages.length > 0) {
      headerElement.content = headerElement.content || {};

      // Preserve existing logo if it exists
      const existingLogo = headerElement.content.logo;

      headerElement.content.navigation = this.pages
        .filter((p: any) => p.active !== false)
        .map(page => {
          const id = this.websiteId || this.templateId;
          const idParam = this.websiteId ? 'websiteId' : 'id';
          const fullPath = id
            ? `/preview?${idParam}=${id}${page.type !== 'home' ? `&pageType=${page.type}` : ''}`
            : '#';
          return this.buildNavItem(page, fullPath);
        });

      // Preserve logo if it was already set
      if (existingLogo) {
        headerElement.content.logo = existingLogo;
      }

      // Ensure CTA button exists
      if (!headerElement.content.ctaButton) {
        headerElement.content.ctaButton = {
          text: 'Book Wash',
          link: '/booking',
          style: 'primary',
        };
      }
    }
  }

  /**
   * Normalize section key by removing numbered suffix (e.g., info_section_1 -> info_section)
   */
  /** Build a single navigation item from a page descriptor and its resolved fullPath. */
  private buildNavItem(
    page: any,
    fullPath: string
  ): { id: string; label: string; link: string; pageType: string; active: boolean } {
    return {
      id: page.id,
      label: page.name,
      link: fullPath,
      pageType: page.type,
      active: page.type === this.currentPageType,
    };
  }

  private normalizeSectionKey(sectionKey: string): string {
    const match = sectionKey.match(/^(.+?)(_\d+)?$/);
    if (match && match[1]) {
      return match[1];
    }
    return sectionKey;
  }

  private static readonly SECTION_KEY_MAPPING: Record<string, string> = {
    hero_section: 'hero',
    info_section: 'welcome',
    testimonials_section: 'testimonials',
    booking_section: 'booking',
    contact_section: 'contact',
    services_section: 'services',
    package_section: 'packages',
    form_section: 'form',
    coupon_card: 'coupon',
    instructions_section: 'instructions',
  };

  /**
   * Build preview elements and visibility mapping from page sections for the website preview page.
   * @example
   * buildElementsFromPageSections({ header: { active: true } }, [], {})
   * undefined
   * @param {Record<string, any>} sections - Mapping of section keys to section data objects.
   * @param {any[]} tempElements - Array to be populated with element objects derived from sections.
   * @param {Record<string, boolean>} tempVisibility - Object to be populated with visibility flags for keys and mapped ids.
   * @returns {void} No return value; tempElements and tempVisibility are mutated in place.
   **/
  private buildElementsFromPageSections(
    sections: Record<string, any>,
    tempElements: any[],
    tempVisibility: Record<string, boolean>
  ): void {
    const mapping = WebsitePreviewPageComponent.SECTION_KEY_MAPPING;
    Object.keys(sections).forEach(key => {
      const normalizedKey = this.normalizeSectionKey(key);
      const mappedId = mapping[normalizedKey] || normalizedKey;
      const section = sections[key];
      const isInfoSectionVariant = normalizedKey === 'info_section';
      const elementId =
        isInfoSectionVariant && key !== 'info_section' ? `${mappedId}_${key}` : mappedId;
      const elementType = isInfoSectionVariant ? 'section' : mappedId;
      tempElements.push({
        id: elementId,
        type: elementType as any,
        content: this.mapSectionContent(key, section),
        selected: false,
        originalKey: key,
      });
      const active = section?.active !== undefined ? section.active : true;
      tempVisibility[key] = active;
      tempVisibility[mappedId] = active;
      tempVisibility[elementId] = active;
    });
  }

  /**
   * Processes template JSON to extract branding, fonts, build header/footer, map page sections into websiteElements, and set defaults and visibility.
   * @example
   * rocessTemplateData(sampleJson)
   * undefined
   * @param {{any}} {{jsonData}} - JSON object from the API containing website and page data used to populate branding, fonts, sections, header/footer, and section visibility.
   * @returns {{void}} Updates component state (colors, fonts, websiteElements, and sectionVisibility) with no return value.
   **/
  processTemplateData(jsonData: any): void {
    // Extract primary color - prioritize website-level branding color over page-level theme color
    if (jsonData?.website?.branding?.colors?.primary) {
      // Website-level branding primary color (highest priority)
      this.primaryColor = jsonData.website.branding.colors.primary;
    } else if (jsonData?.page?.layout?.theme?.primary_color) {
      // Page-level theme primary color (fallback)
      this.primaryColor = jsonData.page.layout.theme.primary_color;
    } else {
      // Default fallback
      this.primaryColor = '#1B5BFF';
    }

    // Extract heading color from website-level branding
    if (jsonData?.website?.branding?.colors?.heading) {
      this.headingColor = jsonData.website.branding.colors.heading;
    } else {
      // Default fallback for headings
      this.headingColor = '#15172E';
    }

    // Extract paragraph color from website-level branding
    if (jsonData?.website?.branding?.colors?.paragraph) {
      this.paragraphColor = jsonData.website.branding.colors.paragraph;
    } else {
      // Default fallback for paragraphs
      this.paragraphColor = '#6B7280';
    }

    // Extract fonts from website-level branding
    if (jsonData?.website?.branding?.fonts?.heading) {
      this.headingFont = jsonData.website.branding.fonts.heading;
    } else {
      // Default fallback for heading font
      this.headingFont = 'Manrope';
    }

    if (jsonData?.website?.branding?.fonts?.body) {
      this.bodyFont = jsonData.website.branding.fonts.body;
    } else {
      // Default fallback for body font
      this.bodyFont = 'Inter';
    }

    // Map API section keys to component element IDs
    if (jsonData?.page?.sections) {
      this.websiteElements = [];
      this.buildElementsFromPageSections(
        jsonData.page.sections,
        this.websiteElements,
        this.sectionVisibility
      );
      // Extract header data from new API structure (website.header)
      const headerContext = {
        websiteId: this.websiteId,
        templateId: this.templateId,
        currentPageType: this.currentPageType,
        convertLinkToPageType: (link: string) => this.convertLinkToPageType(link),
      };
      const headerSection = jsonData?.page?.sections?.header;
      const websiteHeader = jsonData?.website?.header;
      const {
        logo,
        navigation: initialNavigation,
        ctaButton,
      } = buildHeaderFromWebsiteData(jsonData, headerContext, headerSection);
      let navigation = initialNavigation;

      // If navigation is empty, build from pages
      if (navigation.length === 0 && this.pages && this.pages.length > 0) {
        navigation = this.pages
          .filter((p: any) => p.active !== false)
          .map(page => {
            const id = this.websiteId || this.templateId;
            const idParam = this.websiteId ? 'websiteId' : 'id';
            const fullPath = id
              ? `/preview?${idParam}=${id}${page.type !== 'home' ? `&pageType=${page.type}` : ''}`
              : '#';
            return {
              id: page.id,
              label: page.name,
              link: fullPath,
              pageType: page.type,
              active: page.type === this.currentPageType,
            };
          });
      }

      // Ensure hero element is at index 1 (after header)
      // Separate hero from other elements
      const heroElement = this.websiteElements.find(el => el.id === 'hero');
      const otherElements = this.websiteElements.filter(el => el.id !== 'hero');

      // Rebuild elements array with proper order: header (0), hero (1), then others
      this.websiteElements = [];

      // Add header element first (for both template and website preview)
      this.websiteElements.push({
        id: 'header',
        type: 'header' as any,
        content: this.buildHeaderContent(logo, navigation, ctaButton, websiteHeader),
        selected: false,
      });

      // Add hero element second (at index 1) if it exists
      if (heroElement) {
        this.websiteElements.push(heroElement);
      }

      // Add all other elements after hero
      this.websiteElements.push(...otherElements);

      this.sectionVisibility['header'] = true;

      const footerContent = this.buildFooterContentFromWebsite(jsonData);

      this.websiteElements.push({
        id: 'footer',
        type: 'footer' as any,
        content: footerContent,
        selected: false,
      });

      this.sectionVisibility['footer'] = true;
    } else {
      // Extract header data from new API structure (website.header)
      const websiteHeader = jsonData?.website?.header;
      const headerContext = {
        websiteId: this.websiteId,
        templateId: this.templateId,
        currentPageType: this.currentPageType,
        convertLinkToPageType: (link: string) => this.convertLinkToPageType(link),
      };
      const {
        logo,
        navigation: initialNavigation,
        ctaButton,
      } = buildHeaderFromWebsiteData(jsonData, headerContext);
      let navigation = initialNavigation;

      // If navigation is empty, build from pages
      if (navigation.length === 0 && this.pages && this.pages.length > 0) {
        const id = this.websiteId || this.templateId;
        const idParam = this.websiteId ? 'websiteId' : 'id';
        navigation = this.pages
          .filter((p: any) => p.active !== false)
          .map(page => {
            const pageType = page.type === 'home' ? '' : `/${page.type}`;
            const fullPath = id
              ? `/preview?${idParam}=${id}${page.type !== 'home' ? `&pageType=${page.type}` : ''}`
              : `#${pageType}`;
            return this.buildNavItem(page, fullPath);
          });
      }

      const footerContent = this.buildFooterContentFromWebsite(jsonData);

      // Default elements if no sections found
      this.websiteElements = [
        {
          id: 'header',
          type: 'header' as const,
          content: this.buildHeaderContent(logo, navigation, ctaButton, websiteHeader),
          selected: false,
        },
        { id: 'hero', type: 'hero' as const, content: {}, selected: false },
        { id: 'services', type: 'services' as const, content: {}, selected: false },
        { id: 'testimonials', type: 'testimonials' as const, content: {}, selected: false },
        { id: 'contact', type: 'contact' as const, content: {}, selected: false },
        { id: 'footer', type: 'footer' as const, content: footerContent, selected: false },
      ];

      this.sectionVisibility = {
        header: true,
        hero: true,
        services: true,
        testimonials: true,
        contact: true,
        footer: true,
      };
    }
  }

  /**
   * Map an API section object into a normalized component content structure for various section types.
   * @example
   * apSectionContent('hero_section', { title: 'Welcome', description: 'Best service', button: { label: 'Book', link: '/book' }, image: { src: '/hero.png' }, layout: { type: 'layout_2', background_color: '#fff' } })
   * { title: 'Welcome', titleStyle: null, description: 'Best service', buttonText: 'Book', buttonLink: '/book', imageUrl: '/hero.png', layout: 'layout_2', backgroundColor: '#fff' }
   * @param {{string}} {{apiKey}} - The API section key that determines mapping rules (e.g. 'hero_section', 'info_section', 'testimonials_section', 'booking_section', 'contact_section').
   * @param {{any}} {{section}} - The raw section object from the API to be normalized.
   * @returns {{any}} The normalized section content object ready for use by the preview components.
   **/
  mapSectionContent(apiKey: string, section: any): any {
    // Map API section structure to component content structure
    const mapped: any = {};

    // Helper function to extract title (handles both object and string formats)
    const getTitle = (title: any): string => {
      if (typeof title === 'string') return title;
      if (title?.label) return title.label;
      return '';
    };

    // Helper function to extract title style
    const getTitleStyle = (title: any): any => {
      if (typeof title === 'object' && title?.style) {
        return title.style;
      }
      return null;
    };

    // Normalize the key to handle variants like info_section_1, info_section_2, etc.
    const normalizedKey = this.normalizeSectionKey(apiKey);

    if (apiKey === 'hero_section') {
      mapped.title = getTitle(section.title);
      mapped.titleStyle = getTitleStyle(section.title);
      mapped.description = section.description;
      mapped.buttonText = section.button?.label;
      mapped.buttonLink = section.button?.link || section.button?.action || '';
      mapped.imageUrl = section.image?.src;
      // Handle layout type - preserve layout_4, layout_5, etc. format, or convert number to layout_X
      const layoutType = section.layout?.type;
      if (layoutType) {
        if (typeof layoutType === 'string' && layoutType.startsWith('layout_')) {
          mapped.layout = layoutType;
        } else if (typeof layoutType === 'string' && /^[1-6]$/.test(layoutType)) {
          mapped.layout = `layout_${layoutType}`;
        } else {
          mapped.layout = layoutType;
        }
      } else {
        mapped.layout = 'layout_1'; // Default to layout_1 format
      }
      mapped.backgroundColor = section.layout?.background_color;
    } else if (normalizedKey === 'info_section') {
      // Handle info_section, info_section_1, info_section_2, etc.
      mapped.title = getTitle(section.title);
      mapped.titleStyle = getTitleStyle(section.title);
      mapped.description = section.description;
      mapped.buttonText = section.button?.label || section.button?.text;
      mapped.buttonLink = section.button?.link || section.button?.action || '';
      mapped.imageUrl = section.image?.src || section.image?.image;
      // Handle layout type as both number and string
      const layoutType = section.layout?.type || section.layout_type;
      mapped.layout = layoutType ? String(layoutType) : '2';
      mapped.backgroundColor = section.layout?.background_color;
    } else if (apiKey === 'testimonials_section') {
      mapped.title = getTitle(section.title);
      mapped.titleStyle = getTitleStyle(section.title);
      mapped.subtitle = section.subtitle;
      mapped.testimonials = section.testimonials?.map((t: any) => ({
        id: t.name || '',
        text: t.review || '',
        author: {
          name: t.name || '',
          avatar: t.avatar || '',
        },
        rating: t.rating || 5,
      }));
      mapped.backgroundColor = section.layout?.background_color;
      mapped.cardColor = section.layout?.card_color;
    } else if (apiKey === 'booking_section') {
      mapped.title = getTitle(section.title);
      mapped.titleStyle = getTitleStyle(section.title);
      mapped.subtitle = section.subtitle;
      mapped.buttonText = section.button?.label || section.button?.text || 'Book Appointment';
      mapped.buttonLink = section.button?.link || section.button?.action || '';
      mapped.buttonMethod = section.button?.method || 'POST';
      // Extract button colors from button.style (API format) or button.backgroundColor/button.textColor (fallback)
      mapped.buttonBackgroundColor =
        section.button?.style?.background || section.button?.backgroundColor || null;
      mapped.buttonTextColor = section.button?.style?.color || section.button?.textColor || null;
      // Form fields are now static in the layout component, so we don't need to map them
      mapped.backgroundColor = section.layout?.background_color;
    } else if (apiKey === 'contact_section') {
      mapped.title = getTitle(section.title);
      mapped.titleStyle = getTitleStyle(section.title);
      mapped.subtitle = section.subtitle;

      // Extract address - handle location.value format (new API format)
      mapped.address = section.details?.location?.value || section.details?.address || '';

      // Extract phone - API uses details.phone; legacy uses details.contact
      const contactValue = section.details?.phone ?? section.details?.contact;
      mapped.phone =
        typeof contactValue === 'string'
          ? contactValue
          : contactValue && typeof contactValue === 'object'
            ? (contactValue as any).value ||
              (contactValue as any).phone ||
              (contactValue as any).label ||
              ''
            : '';

      // Extract email - handle both string and object formats
      const emailValue = section.details?.email;
      mapped.email =
        typeof emailValue === 'string'
          ? emailValue
          : emailValue && typeof emailValue === 'object'
            ? (emailValue as any).value ||
              (emailValue as any).email ||
              (emailValue as any).label ||
              ''
            : '';

      mapped.mapEmbedUrl = section.map_embed_url;
      mapped.businessHours = mapContactBusinessHours(section);
      mapped.socialLinks = section.social_links;
      mapped.backgroundColor = section.layout?.background_color;
    }

    return mapped;
  }

  /**
   * Builds an array of footer quick link objects from a configuration, resolving preview URLs when a website or template id is present.
   * @example
   * buildFooterQuickLinks({ column_1: [{ label: 'Home', link: '/' }], column_2: [{ label: 'Contact', link: '/contact' }] })
   * [{ label: 'Home', link: '/preview?websiteId=123', pageType: 'home' }, { label: 'Contact', link: '/preview?websiteId=123&pageType=contact', pageType: 'contact' }]
   * @param {{any}} {{quickLinksConfig}} - Configuration object containing optional column_1 and column_2 arrays of link descriptors.
   * @returns {{any[]}} Array of normalized link objects, each containing label, link, and pageType.
   **/
  private buildFooterQuickLinks(quickLinksConfig: any): any[] {
    if (!quickLinksConfig) {
      return [];
    }

    const id = this.websiteId || this.templateId;
    const idParam = this.websiteId ? 'websiteId' : 'id';
    /**
     * Convert an array of link objects into preview-ready navigation links; each item is normalized to include label, resolved preview link and pageType.
     * @example
     * transformLinks([{ label: 'Home', link: '/' }])
     * [{ label: 'Home', link: '/', pageType: 'home' }]
     * @param {{any[]}} {{links}} - Array of link objects to transform (each object may have a 'link' and a 'label').
     * @returns {{Array.<{label: string, link: string, pageType: string}>}} Array of transformed link objects with label, link and pageType.
     **/
    const mapLinks = (links: any[]) =>
      (links || [])
        .filter((link: any) => !!link && typeof link === 'object')
        .map((link: any) => {
          const navLink = link.link || '#';
          if (isExternalFooterHref(navLink) || navLink.startsWith('/preview')) {
            return {
              label: link.label || '',
              link: navLink,
              pageType: link.pageType || this.convertLinkToPageType(navLink),
            };
          }
          const pageType = link.pageType || this.convertLinkToPageType(navLink);
          const fullPath =
            navLink === '/' || navLink === '#' || pageType === 'home'
              ? id
                ? `/preview?${idParam}=${id}`
                : navLink
              : id
                ? `/preview?${idParam}=${id}&pageType=${pageType}`
                : navLink;

          return {
            label: link.label || '',
            link: fullPath,
            pageType,
          };
        });

    let storedLinks: any[] = [];
    if (Array.isArray(quickLinksConfig.column_1)) {
      storedLinks = storedLinks.concat(quickLinksConfig.column_1);
    }
    if (Array.isArray(quickLinksConfig.column_2)) {
      storedLinks = storedLinks.concat(quickLinksConfig.column_2);
    }

    return mapLinks(resolveFooterQuickLinks(storedLinks, this.pages));
  }

  private buildHeaderContent(logo: any, navigation: any, ctaButton: any, websiteHeader: any): any {
    return {
      logo,
      navigation,
      ctaButton,
      topBorder: websiteHeader?.top_border,
      backgroundColor: websiteHeader?.background_color,
      navigationStyle: websiteHeader?.navigation_style,
      layout: websiteHeader?.layout,
    };
  }

  /**
   * Build a normalized footer content object from a website JSON payload.
   * @example
   * buildFooterContentFromWebsite({ website: { footer: { logo: { text: 'Way', url: 'logo.png' }, quick_links: [], social_links: { platforms: [] } } } })
   * { logo: { text: 'Way', image: 'logo.png', alt: 'Logo' }, quickLinks: [], quickLinksTitle: 'Quick links', socialLinks: [], socialLinksTitle: 'Connect with us', copyright: `Copyright © ${new Date().getFullYear()} way.com. All rights reserved.`, backgroundColor: '#000920', textColor: '#FFFFFF', linkColor: '#FFFFFF', separatorColor: 'rgba(255, 255, 255, 0.1)' }
   * @param {{any}} {{jsonData}} - Input website JSON object that may contain footer configuration.
   * @returns {{any}} Normalized footer content object suitable for rendering the website footer.
   **/
  private buildFooterContentFromWebsite(jsonData: any): any {
    const websiteFooter = jsonData?.website?.footer;
    const footerContent: any = {};

    if (!websiteFooter) {
      return footerContent;
    }

    let logo: any = { text: 'way' };
    if (websiteFooter.logo) {
      const footerLogoImage = websiteFooter.logo.url || websiteFooter.logo.image || '';
      logo = footerLogoImage
        ? {
            text: websiteFooter.logo.text || 'way',
            image: footerLogoImage,
            alt: websiteFooter.logo.alt || 'Logo',
          }
        : {
            text: websiteFooter.logo.text || 'way',
          };
    }
    footerContent.logo = logo;

    const quickLinks = this.buildFooterQuickLinks(websiteFooter.quick_links);
    footerContent.quickLinks = quickLinks;
    footerContent.quickLinksTitle = websiteFooter.quick_links?.title || 'Quick links';

    let socialLinks: any[] = [];
    if (
      websiteFooter.social_links?.platforms &&
      Array.isArray(websiteFooter.social_links.platforms)
    ) {
      socialLinks = websiteFooter.social_links.platforms.map((social: any) => ({
        platform: social.platform || '',
        url: social.url || social.link || '#',
        icon: social.icon || social.platform?.toLowerCase() || 'link',
      }));
    }
    footerContent.socialLinks = socialLinks;
    footerContent.socialLinksTitle = websiteFooter.social_links?.title || 'Connect with us';

    footerContent.copyright =
      websiteFooter.copyright?.text ||
      (typeof websiteFooter.copyright === 'string' ? websiteFooter.copyright : null) ||
      `Copyright © ${new Date().getFullYear()} way.com. All rights reserved.`;

    footerContent.backgroundColor =
      websiteFooter.theme?.background_color || websiteFooter.backgroundColor || '#000920';
    footerContent.textColor = websiteFooter.theme?.text_color || '#FFFFFF';
    footerContent.linkColor = websiteFooter.theme?.link_color || '#FFFFFF';
    footerContent.separatorColor =
      websiteFooter.theme?.separator_color || 'rgba(255, 255, 255, 0.1)';

    return footerContent;
  }

  /**
   * Convert a button link to a page type for navigation
   * Handles links like "/express-washes", "express-washes", "/", etc.
   */
  convertLinkToPageType(link: string | undefined): string {
    return convertLinkToPageTypeFromUtil(link, this.pages || [], (name: string) =>
      this.getPageType(name)
    );
  }

  onElementSelect(_event: any): void {
    // Element selection is disabled in preview mode
  }

  getPageType(pageName: string): string {
    return pageNameToPageType(pageName);
  }

  /** Merged page JSON for preview components (matches website-builder pageData input). */
  getPreviewPageData(): any {
    const raw = this.currentPageData?.json_data ?? this.templateData?.json_data;
    if (!raw) {
      return null;
    }
    return mergeWebsiteJsonIntoPage(raw, this.sharedWebsiteJson);
  }

  private applySharedBranding(websiteJson: any | null): void {
    applyBrandingFromWebsiteJson(websiteJson, {
      setPrimaryColor: value => {
        this.primaryColor = value;
      },
      setHeadingColor: value => {
        this.headingColor = value;
      },
      setParagraphColor: value => {
        this.paragraphColor = value;
      },
      setHeadingFont: value => {
        this.headingFont = value;
      },
      setBodyFont: value => {
        this.bodyFont = value;
      },
    });
  }

  private getMergedPageJson(page: any): any {
    return mergeWebsiteJsonIntoPage(page?.json_data, this.sharedWebsiteJson);
  }

  /**
   * Handle a navigation click: normalize pageType, avoid duplicate navigation, use cached page data or load/process the page and finalize navigation.
   * @example
   * nNavigationClick('home')
   * undefined
   * @param {{string}} {{pageType}} - The page type or identifier (e.g. type, slug, or page name) to navigate to.
   * @returns {{void}} Does not return a value; updates component state and triggers loading/finalization.
   **/
  onNavigationClick(pageType: string): void {
    console.log('Navigation clicked:', pageType);

    // Prevent duplicate navigation calls
    if (this.isNavigating) {
      console.log('Navigation already in progress, ignoring duplicate click');
      return;
    }

    // Normalize pageType - try to find matching page in pages array
    let normalizedPageType = pageType;
    const matchingPage = this.pages.find(p => {
      return (
        p.type === pageType ||
        p.name.toLowerCase().replace(/\s+/g, '-') === pageType.toLowerCase() ||
        this.getPageType(p.name) === pageType
      );
    });

    if (matchingPage) {
      normalizedPageType = matchingPage.type;
    } else if (isKnownFooterPageType(pageType)) {
      normalizedPageType = normalizeFooterPageType(pageType);
    } else {
      // Fallback to getPageType if no match found
      normalizedPageType = this.getPageType(pageType);
    }

    // Prevent navigation if already on the same page
    if (this.currentPageType === normalizedPageType) {
      console.log('Already on page:', normalizedPageType, '- skipping navigation');
      // Still update header navigation active state to ensure it's correct
      this.updateHeaderNavigation();
      return;
    }

    this.isNavigating = true;

    // Check if we have cached data for this page
    const cachedPageData = this.cachedPages.get(normalizedPageType);
    const cachedElements = this.cachedElements.get(normalizedPageType);
    const cachedVisibility = this.cachedSectionVisibility.get(normalizedPageType);

    if (cachedPageData && cachedElements && cachedVisibility && cachedElements.length > 0) {
      // Use cached data for instant navigation
      this.currentPageType = normalizedPageType;
      this.currentPageData = cachedPageData;
      // Create deep copies to avoid reference issues
      this.websiteElements = JSON.parse(JSON.stringify(cachedElements));
      this.sectionVisibility = JSON.parse(JSON.stringify(cachedVisibility));

      this.finalizeNavigation(normalizedPageType);
      return;
    }

    // If no cached data, try to find the page and process it
    const pageToLoad = this.pages.find(p => p.type === normalizedPageType);
    if (pageToLoad && pageToLoad.json_data) {
      // Preserve header and footer from current elements
      const currentHeader = this.websiteElements.find(el => el.id === 'header');
      const currentFooter = this.websiteElements.find(el => el.id === 'footer');

      // Process this page's data immediately
      const tempElements: any[] = [];
      const tempVisibility: Record<string, boolean> = {};
      const jsonData = this.getMergedPageJson(pageToLoad);

      // Add header first (preserve existing header or create new one)
      if (currentHeader) {
        tempElements.push(JSON.parse(JSON.stringify(currentHeader)));
        tempVisibility['header'] = this.sectionVisibility['header'] !== false;
      }

      if (jsonData?.page?.sections) {
        this.buildElementsFromPageSections(jsonData.page.sections, tempElements, tempVisibility);
      }

      // Add footer last (preserve existing footer or create new one)
      if (currentFooter) {
        tempElements.push(JSON.parse(JSON.stringify(currentFooter)));
        tempVisibility['footer'] = this.sectionVisibility['footer'] !== false;
      }

      // Cache and use the data
      this.cachePageData(normalizedPageType, pageToLoad, tempElements, tempVisibility);
      this.currentPageType = normalizedPageType;
      this.currentPageData = pageToLoad;
      this.websiteElements = JSON.parse(JSON.stringify(tempElements));
      this.sectionVisibility = JSON.parse(JSON.stringify(tempVisibility));

      this.finalizeNavigation(normalizedPageType);
      return;
    }

    // Last resort: reload data (shouldn't happen if pages are properly loaded)
    console.warn('No cached data found for page type:', normalizedPageType, 'Reloading...');
    this.currentPageType = normalizedPageType;

    // Reset navigation flag before async operation
    this.isNavigating = false;

    if (this.websiteId) {
      this.loadWebsiteData(normalizedPageType);
    } else if (this.templateId) {
      this.loadTemplateData(normalizedPageType);
    }
  }

  // Determine which template theme to display
  isMinimalTemplate(): boolean {
    return (
      this.templateId === 'e52c4811-f3f9-4f64-8cdb-520396dbeac7' ||
      !this.templateId ||
      (!this.isModernTemplate() && !this.isDarkTemplate() && !this.isChampsTemplate())
    );
  }

  isModernTemplate(): boolean {
    return this.templateId === '71715e65-dc16-49b6-ac7a-0bb75fee6a2e';
  }

  isDarkTemplate(): boolean {
    return this.templateId === '808371ee-5130-4e24-8334-69646e403de4';
  }

  isChampsTemplate(): boolean {
    return this.templateId === 'c97e1e03-b1c5-47fb-b753-27cd541cfdf8';
  }

  /**
   * Cache page data for smooth navigation
   */
  /** Shared post-processing tail run by loadWebsiteData and loadTemplateData after pages are parsed. */
  private finalizeDataLoad(): void {
    try {
      this.cachePageData(
        this.currentPageType,
        this.currentPageData,
        this.websiteElements,
        this.sectionVisibility
      );
      this.preloadAllPages();
      this.updateHeaderNavigation();
    } catch (err) {
      console.error('Error finalizing website preview:', err);
    }
    this.isLoading = false;
    console.log('Processed pages:', this.pages);
    console.log('Current page type:', this.currentPageType);
    console.log('Website elements:', this.websiteElements);
    console.log('Section visibility:', this.sectionVisibility);
  }

  private cachePageData(pageType: string, pageData: any, elements: any[], visibility: any): void {
    this.cachedPages.set(pageType, pageData);
    this.cachedElements.set(pageType, JSON.parse(JSON.stringify(elements))); // Deep copy
    this.cachedSectionVisibility.set(pageType, JSON.parse(JSON.stringify(visibility))); // Deep copy
  }

  /**
   * Pre-load and cache all pages for instant navigation
   */
  private preloadAllPages(): void {
    // Get header and footer from current elements to preserve them across pages
    const currentHeader = this.websiteElements.find(el => el.id === 'header');
    const currentFooter = this.websiteElements.find(el => el.id === 'footer');

    // Process each page and cache its data
    this.pages.forEach((page: any) => {
      const pageType = page.type;

      // Skip if already cached (current page is already processed and cached)
      if (this.cachedPages.has(pageType)) {
        return;
      }

      // Process the page's JSON data
      if (page.json_data) {
        const tempElements: any[] = [];
        const tempVisibility: Record<string, boolean> = {};

        // Add header first (preserve existing header)
        if (currentHeader) {
          tempElements.push(JSON.parse(JSON.stringify(currentHeader)));
          tempVisibility['header'] = this.sectionVisibility['header'] !== false;
        }

        const jsonData = this.getMergedPageJson(page);

        if (jsonData?.page?.sections) {
          this.buildElementsFromPageSections(jsonData.page.sections, tempElements, tempVisibility);
        }

        // Add footer last (preserve existing footer)
        if (currentFooter) {
          tempElements.push(JSON.parse(JSON.stringify(currentFooter)));
          tempVisibility['footer'] = this.sectionVisibility['footer'] !== false;
        }

        // Cache the processed data (even if no sections, cache the page data)
        this.cachePageData(pageType, page, tempElements, tempVisibility);
      } else {
        // Cache page even if no json_data (for pages that might not have sections)
        // Still include header and footer
        const tempElements: any[] = [];
        const tempVisibility: Record<string, boolean> = {};

        if (currentHeader) {
          tempElements.push(JSON.parse(JSON.stringify(currentHeader)));
          tempVisibility['header'] = true;
        }
        if (currentFooter) {
          tempElements.push(JSON.parse(JSON.stringify(currentFooter)));
          tempVisibility['footer'] = true;
        }

        this.cachePageData(pageType, page, tempElements, tempVisibility);
      }
    });
  }

  /**
   * Detect device type based on viewport width
   */
  private detectDeviceType(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const width = window.innerWidth;

    if (width <= 480) {
      this.deviceType = 'mobile';
    } else if (width <= 768) {
      this.deviceType = 'tablet';
    } else {
      this.deviceType = 'desktop';
    }
  }

  ngOnDestroy(): void {
    // Clean up resize listener
    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
    }
  }
}
